import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Landmark, Calculator, Handshake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `€ ${new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(n)}`;

const inputCls =
  "w-full bg-white border border-[var(--ink)]/20 rounded-md p-3 text-sm focus:border-[var(--gold)] outline-none transition-colors";
const labelCls = "block font-mono-lbl text-xs uppercase tracking-wider text-[var(--ink)]/80 mb-2";

export default function Mutui() {
  const [calc, setCalc] = useState({ prezzo: "250000", percentuale: "80", tasso: "3.2", durata: "25", email: "" });
  const [rata, setRata] = useState(null);
  const [lead, setLead] = useState({ nominativo: "", telefono: "", email: "", note: "" });
  const [sending, setSending] = useState(false);

  const upCalc = (k) => (e) => setCalc({ ...calc, [k]: e.target.value });
  const upLead = (k) => (e) => setLead({ ...lead, [k]: e.target.value });

  const calcolaRata = (e) => {
    e.preventDefault();
    const prezzo = parseFloat(calc.prezzo);
    const perc = parseFloat(calc.percentuale);
    const tasso = parseFloat(calc.tasso);
    const anni = parseInt(calc.durata, 10);
    if (!prezzo || prezzo <= 0 || !perc || perc <= 0 || perc > 100 || isNaN(tasso) || tasso < 0 || !anni) {
      toast.error("Controlla i valori inseriti per il calcolo della rata.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(calc.email)) {
      toast.error("Inserisci la tua email per vedere la rata: ti invieremo il dettaglio della simulazione.");
      return;
    }
    const capitale = (prezzo * perc) / 100;
    const i = tasso / 100 / 12;
    const n = anni * 12;
    const mensile = i === 0 ? capitale / n : (capitale * i) / (1 - Math.pow(1 + i, -n));
    setRata({
      capitale,
      mensile,
      totale: mensile * n,
      interessi: mensile * n - capitale,
      anni,
    });
    // salva il contatto di chi simula (silenzioso)
    axios
      .post(`${API}/leads`, {
        nominativo: "Simulazione mutuo online",
        telefono: "",
        richiesta: "Simulazione rata mutuo",
        note: `Mutuo ${eur(capitale)} in ${anni} anni al ${tasso}% — rata ${eur(mensile)}/mese su immobile da ${eur(prezzo)}`,
        email: calc.email,
      })
      .catch(() => {});
  };

  const inviaRichiesta = async (e) => {
    e.preventDefault();
    if (!lead.nominativo.trim() || !lead.telefono.trim()) {
      toast.error("Inserisci nominativo e telefono per essere ricontattato dal broker.");
      return;
    }
    setSending(true);
    const dettagli = [];
    if (rata) {
      dettagli.push(
        `Simulazione: mutuo ${eur(rata.capitale)} in ${rata.anni} anni, rata ${eur(rata.mensile)}/mese`
      );
    }
    if (lead.note.trim()) dettagli.push(lead.note.trim());
    try {
      await axios.post(`${API}/leads`, {
        nominativo: lead.nominativo,
        telefono: lead.telefono,
        richiesta: "Richiesta consulenza mutuo",
        note: dettagli.join(" — "),
        email: lead.email,
      });
      toast.success("Richiesta inviata. Il broker mutui ti ricontatterà a breve.");
      setLead({ nominativo: "", telefono: "", email: "", note: "" });
    } catch (err) {
      toast.error("Invio non riuscito. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section className="bg-white py-24 md:py-32 px-6 border-b border-[var(--ink)]/10" data-testid="mutui-page">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-deep)] block mb-2">
                Broker Mutui in Agenzia
              </span>
              <h1 className="font-display text-[clamp(32px,3.8vw,52px)] font-bold leading-tight">
                Mutui: rata chiara, consulenza vera.
              </h1>
            </div>
            <p className="font-mono-lbl text-xs text-[var(--ink)]/60 max-w-xs">
              Simula la rata in autonomia, poi parla con il nostro broker creditizio in sede a Modena.
            </p>
          </div>

          {/* CALCOLATORE MUTUO */}
          <div className="bg-[var(--paper)] border border-[var(--ink)]/15 rounded-xl p-8 md:p-10 shadow-sm">
            <form onSubmit={calcolaRata} data-testid="mortgage-form">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label htmlFor="mt-prezzo" className={labelCls}>Prezzo Immobile (€)</label>
                  <input id="mt-prezzo" data-testid="mortgage-prezzo" type="number" min="1" value={calc.prezzo} onChange={upCalc("prezzo")} className={`${inputCls} font-mono-lbl`} />
                </div>
                <div>
                  <label htmlFor="mt-perc" className={labelCls}>% Finanziata</label>
                  <select id="mt-perc" data-testid="mortgage-percentuale" value={calc.percentuale} onChange={upCalc("percentuale")} className={inputCls}>
                    <option value="100">100% del prezzo</option>
                    <option value="90">90% del prezzo</option>
                    <option value="80">80% del prezzo</option>
                    <option value="70">70% del prezzo</option>
                    <option value="60">60% del prezzo</option>
                    <option value="50">50% del prezzo</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="mt-tasso" className={labelCls}>Tasso Annuo (%)</label>
                  <input id="mt-tasso" data-testid="mortgage-tasso" type="number" step="0.1" min="0" value={calc.tasso} onChange={upCalc("tasso")} className={`${inputCls} font-mono-lbl`} />
                </div>
                <div>
                  <label htmlFor="mt-durata" className={labelCls}>Durata</label>
                  <select id="mt-durata" data-testid="mortgage-durata" value={calc.durata} onChange={upCalc("durata")} className={inputCls}>
                    <option value="10">10 anni</option>
                    <option value="15">15 anni</option>
                    <option value="20">20 anni</option>
                    <option value="25">25 anni</option>
                    <option value="30">30 anni</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-end justify-between gap-4 pt-6 mt-6 border-t border-[var(--ink)]/10">
                <div className="w-full sm:flex-1">
                  <label htmlFor="mt-email" className={labelCls}>La tua email — serve per vedere la rata</label>
                  <input id="mt-email" data-testid="mortgage-email" type="email" placeholder="nome@email.it" value={calc.email} onChange={upCalc("email")} className={inputCls} />
                  <span className="block text-[11px] text-[var(--ink)]/60 mt-1 font-mono-lbl">
                    Ti inviamo il piano dettagliato e le migliori offerte del momento.
                  </span>
                </div>
                <button
                  type="submit"
                  data-testid="mortgage-submit"
                  className="w-full sm:w-auto px-8 py-3.5 bg-[var(--ink)] text-[var(--gold-light)] font-mono-lbl text-xs uppercase tracking-widest font-bold rounded hover:bg-[var(--gold)] hover:text-[var(--ink)] transition-colors"
                >
                  Calcola Rata
                </button>
              </div>
            </form>

            {rata && (
              <div
                data-testid="mortgage-result"
                className="mt-8 bg-[var(--ink)] text-white rounded-lg p-8 grid grid-cols-1 md:grid-cols-4 gap-6 items-center"
              >
                <div>
                  <span className="font-mono-lbl text-[10px] uppercase tracking-widest text-[var(--gold-light)] block mb-1">
                    Rata mensile
                  </span>
                  <span className="font-display font-black text-2xl md:text-3xl" data-testid="mortgage-rata">
                    {eur(rata.mensile)}
                  </span>
                </div>
                <div className="font-mono-lbl text-xs space-y-1 text-white/75">
                  <p>Capitale finanziato: <span className="text-[var(--gold-light)]">{eur(rata.capitale)}</span></p>
                  <p>Durata: {rata.anni} anni</p>
                </div>
                <div className="font-mono-lbl text-xs space-y-1 text-white/75">
                  <p>Totale rimborsato: {eur(rata.totale)}</p>
                  <p>Interessi complessivi: {eur(rata.interessi)}</p>
                </div>
                <div>
                  <a
                    href="#consulenza-mutuo"
                    data-testid="mortgage-result-cta"
                    className="inline-block px-5 py-2.5 bg-[var(--gold)] text-[var(--ink)] font-mono-lbl text-xs uppercase tracking-widest font-bold rounded hover:bg-white transition-colors"
                  >
                    Consulenza Gratuita →
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* VANTAGGI BROKER */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 font-mono-lbl text-xs">
            <div className="bg-[var(--paper)] border border-[var(--ink)]/10 rounded p-4">
              <Landmark className="w-4 h-4 text-[var(--gold-deep)] mb-2" />
              <span className="text-[var(--gold-deep)] font-bold block mb-1 uppercase">Broker in sede</span>
              <span className="text-[var(--ink)]/70">Un broker creditizio dedicato ti segue in agenzia, dalla pratica al rogito.</span>
            </div>
            <div className="bg-[var(--paper)] border border-[var(--ink)]/10 rounded p-4">
              <Calculator className="w-4 h-4 text-[var(--gold-deep)] mb-2" />
              <span className="text-[var(--gold-deep)] font-bold block mb-1 uppercase">Confronto banche</span>
              <span className="text-[var(--ink)]/70">Confrontiamo le offerte di più istituti per trovare rata e condizioni migliori.</span>
            </div>
            <div className="bg-[var(--paper)] border border-[var(--ink)]/10 rounded p-4">
              <Handshake className="w-4 h-4 text-[var(--gold-deep)] mb-2" />
              <span className="text-[var(--gold-deep)] font-bold block mb-1 uppercase">Un solo referente</span>
              <span className="text-[var(--ink)]/70">Compravendita e mutuo coordinati dalla stessa agenzia, senza rimpalli.</span>
            </div>
          </div>
          {/* SURROGA / RINEGOZIAZIONE */}
          <div className="mt-8 bg-[var(--ink)] text-white rounded-xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-light)] block mb-2">
                Hai già un mutuo?
              </span>
              <h2 className="font-display font-bold text-2xl leading-tight mb-2">
                Surroga e rinegoziazione: la rata può scendere.
              </h2>
              <p className="text-sm text-white/75 leading-relaxed max-w-xl">
                Se i tassi sono cambiati da quando hai firmato, il nostro broker valuta gratuitamente la surroga del
                tuo mutuo esistente: porti il debito in una banca con condizioni migliori senza costi notarili aggiuntivi.
              </p>
            </div>
            <a
              href="#consulenza-mutuo"
              data-testid="surroga-cta"
              className="flex-shrink-0 px-8 py-4 bg-[var(--gold)] text-[var(--ink)] font-mono-lbl text-xs uppercase tracking-widest font-bold rounded hover:bg-white transition-colors"
            >
              Valuta la Surroga →
            </a>
          </div>
        </div>
      </section>
      <section id="consulenza-mutuo" className="bg-[var(--ink)] text-white py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-light)] block">
              Consulenza Mutuo
            </span>
            <h2 className="font-display font-bold text-[clamp(32px,3.8vw,52px)] leading-tight">
              Parla subito con il broker.
            </h2>
            <p className="text-sm text-white/75 leading-relaxed max-w-md">
              Lascia i tuoi dati: ti richiamiamo per una prima consulenza mutuo gratuita e senza impegno, in agenzia a
              Modena o al telefono.
            </p>
            <div className="pt-4 border-t border-white/10 text-[11px] text-white/60 space-y-1 font-mono-lbl">
              <p className="font-semibold text-white/80">Via Giardini 1053, 41122 Modena</p>
              <p>Lun–Ven 09:00–20:00 · Sab 09:00–12:00</p>
              <p>Tel e WhatsApp: +39 334 960 4516</p>
            </div>
          </div>

          <div className="lg:col-span-7 bg-white text-[var(--ink)] p-8 md:p-10 rounded-xl">
            <h3 className="font-display font-bold text-2xl mb-1">Richiedi Consulenza Mutuo</h3>
            <p className="font-mono-lbl text-xs text-[var(--ink)]/60 mb-6 uppercase">
              Ti ricontatta il broker creditizio Ascari
            </p>
            <form className="space-y-4" onSubmit={inviaRichiesta} data-testid="mutuo-lead-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="ml-nome" className="block font-mono-lbl text-[11px] uppercase tracking-wider mb-1">
                    Nominativo
                  </label>
                  <input
                    id="ml-nome"
                    data-testid="mutuo-lead-name"
                    type="text"
                    placeholder="Nome completo"
                    value={lead.nominativo}
                    onChange={upLead("nominativo")}
                    className="w-full bg-[var(--paper)] border border-[var(--ink)]/20 rounded p-2.5 text-sm focus:border-[var(--gold)] outline-none transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="ml-tel" className="block font-mono-lbl text-[11px] uppercase tracking-wider mb-1">
                    Telefono
                  </label>
                  <input
                    id="ml-tel"
                    data-testid="mutuo-lead-phone"
                    type="tel"
                    placeholder="Cellulare"
                    value={lead.telefono}
                    onChange={upLead("telefono")}
                    className="w-full bg-[var(--paper)] border border-[var(--ink)]/20 rounded p-2.5 text-sm focus:border-[var(--gold)] outline-none transition-colors"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="ml-email" className="block font-mono-lbl text-[11px] uppercase tracking-wider mb-1">
                    Email
                  </label>
                  <input
                    id="ml-email"
                    data-testid="mutuo-lead-email"
                    type="email"
                    placeholder="nome@email.it"
                    value={lead.email}
                    onChange={upLead("email")}
                    className="w-full bg-[var(--paper)] border border-[var(--ink)]/20 rounded p-2.5 text-sm focus:border-[var(--gold)] outline-none transition-colors"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="ml-note" className="block font-mono-lbl text-[11px] uppercase tracking-wider mb-1">
                  Note (importo desiderato, tempistiche, prima casa...)
                </label>
                <textarea
                  id="ml-note"
                  data-testid="mutuo-lead-notes"
                  rows="3"
                  placeholder="Es. prima casa, vorrei un mutuo di € 180.000 in 25 anni..."
                  value={lead.note}
                  onChange={upLead("note")}
                  className="w-full bg-[var(--paper)] border border-[var(--ink)]/20 rounded p-2.5 text-sm focus:border-[var(--gold)] outline-none transition-colors"
                ></textarea>
              </div>
              {rata && (
                <p className="font-mono-lbl text-[11px] text-[var(--gold-deep)]" data-testid="mutuo-lead-sim">
                  Alleghiamo la tua simulazione: mutuo {eur(rata.capitale)} in {rata.anni} anni, rata {eur(rata.mensile)}/mese.
                </p>
              )}
              <button
                type="submit"
                disabled={sending}
                data-testid="mutuo-lead-submit"
                className="w-full py-3.5 bg-[var(--ink)] text-[var(--gold-light)] font-mono-lbl text-xs uppercase tracking-widest font-bold rounded hover:bg-[var(--gold)] hover:text-[var(--ink)] transition-colors disabled:opacity-60"
              >
                {sending ? "Invio in corso..." : "Invia Richiesta di Consulenza"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
